"use client";

import { useState, useEffect } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import Cookies from 'js-cookie';
import ToggleSwitch from './toggleSwitch';
import classNames from "classnames";

type Preferences = {
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
};

const PrivacyConsentHandler = () => {
  const [showManagePreferences, setShowManagePreferences] = useState(false);
  const [preferences, setPreferences] = useState<Preferences>({ functional: false, analytics: false, marketing: false });
  const [isVisible, setIsVisible] = useState(false);
  const t = useTranslations('privacyConsent');
  const [consentAcquired, setConsentAcquired] = useState<boolean | undefined>(undefined);
  const [savingConsent, setSavingConsent] = useState<false | "confirm-selection" | "accept-all">(false);

  const saveConsent = async (pref: Preferences) => {
    Cookies.set("prvc-cnsnt", JSON.stringify(pref));

    // Bing
    window.uetq = window.uetq || [];
    window.uetq.push('consent', 'update', {
      'ad_storage': pref.marketing ? 'granted' : 'denied'
    });

    setConsentAcquired(true);
    setSavingConsent(false);
  };

  useEffect(() => {
    try {
      const consentCookie = Cookies.get("prvc-cnsnt");
      if (!consentCookie)
        throw new Error("No consent found");

      const consentJSON = JSON.parse(consentCookie);
      if (typeof consentJSON.functional !== "boolean" || typeof consentJSON.analytics !== "boolean" || typeof consentJSON.marketing !== "boolean")
        throw new Error("Invalid consent");

      const consent: Preferences = {
        functional: consentJSON.functional,
        analytics: consentJSON.analytics,
        marketing: consentJSON.marketing,
      };
      setPreferences(consent);
      Cookies.set("prvc-cnsnt", JSON.stringify(consent));
      saveConsent(consent).then(() => {
        setConsentAcquired(true);
      });

    } catch (err) {
      setConsentAcquired(false);
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = async () => {
    setSavingConsent("accept-all");
    setPreferences({ functional: true, analytics: true, marketing: true });
    await saveConsent({ functional: true, analytics: true, marketing: true });
    setIsVisible(false);
    setShowManagePreferences(false);
  };

  const handleConfirmSelection = async () => {
    setSavingConsent("confirm-selection");
    await saveConsent(preferences);
    setIsVisible(false);
    setShowManagePreferences(false);
  };

  if (consentAcquired !== false) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black bg-opacity-50"></div>

      <div className="flex fixed inset-0 z-50 justify-center items-end w-full h-screen md:items-center">
        <div className={`bg-white p-10 rounded-t-3xl md:rounded-3xl md:max-w-6xl shadow-lg w-full transform transition-all duration-250 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'}`}>
          <div className={classNames({ "hidden": showManagePreferences }, "py-10 md:block md:py-0")}>
            <Image src="/lock.svg" alt="lock image" width={168} height={135} className="mx-auto mb-4 md:hidden" />
            <h1 className="mb-4 text-2xl font-bold text-center md:text-left">{t('title')}</h1>

            <p className="text-[#6B6B6B] text-sm mb-8">
              {t.rich('description1', {
                link: (chunks) => (
                  <a href="https://www.rent80.com/cookie-policy" target="_blank" rel="noopener noreferrer" className='text-[#0b0aad] font-semibold hover:underline'>
                    {chunks}
                  </a>
                )
              })}
            </p>
            {/* // TODO: Set up the Privacy setting page */}
            {/* <p className="text-[#6B6B6B] text-sm md:mb-8">
              {t.rich('description2', {
                link: (chunks) => (
                  <a href="#" target="_blank" rel="noopener noreferrer" className='text-[#46009F] font-semibold hover:underline'>
                    {chunks}
                  </a>
                )
              })}
            </p> */}
          </div>

          <div className={classNames({ "hidden": !showManagePreferences }, "md:hidden")}>
            <h1 className="mb-1 text-2xl font-bold text-left">
              {t("managePreferences")}
            </h1>
            <p className="mb-4 text-xs text-gray-500 md:text-sm">
              {t("managePreferencesDescription")}
            </p>
          </div>
          <div className={classNames(showManagePreferences ? "grid" : "hidden", "md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mb-8 rounded-xl md:border divide-y md:divide-y-0 lg:!divide-x")}>
            <div className="py-4 md:p-4 md:border-e md:border-b lg:border-0">
              <ToggleSwitch
                title={t("essential")}
                description={t("essentialDescription")}
                checked={true}
                disabled
              />
            </div>

            <div className="py-4 md:p-4 md:!border-b lg:!border-b-0">
              <ToggleSwitch
                title={t("functional")}
                description={t("functionalDescription")}
                checked={preferences.functional}
                onChange={() => setPreferences({ ...preferences, functional: !preferences.functional })}
              />
            </div>

            <div className="py-4 md:p-4 md:border-e lg:border-0">
              <ToggleSwitch
                title={t("analytics")}
                description={t("analyticsDescription")}
                checked={preferences.analytics}
                onChange={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
              />
            </div>

            <div className="py-4 md:p-4 lg:border-0">
              <ToggleSwitch
                title={t("marketing")}
                description={t("marketingDescription")}
                checked={preferences.marketing}
                onChange={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
              />
            </div>
          </div>

          <div className="flex flex-col gap-4 justify-center w-full md:flex-row-reverse sm:justify-between">
            <button
              id="accept-all"
              className="bg-[#0b0aad] text-white md:py-4 py-3 px-8 rounded-xl font-semibold w-full active:scale-95 duration-300 transition-all active:bg-opacity-90 disabled:opacity-50"
              disabled={savingConsent !== false}
              onClick={handleAcceptAll}
            >
              {t("allowAll")}
            </button>
            <button
              id="confirm-selection"
              className={classNames({ "hidden": !showManagePreferences }, "md:block bg-[#D6EAFF] md:py-4 py-3 px-8 rounded-xl font-semibold w-full active:scale-95 duration-300 transition-all active:bg-gray-200 disabled:opacity-50")}
              disabled={savingConsent !== false}
              onClick={handleConfirmSelection}
            >
              {t("allowSelected")}
            </button>
            {!showManagePreferences &&
              <button
                className="md:hidden bg-[#D6EAFF] py-3 px-8 rounded-xl font-semibold w-full active:scale-95 duration-300 transition-all active:bg-gray-200"
                onClick={() => {
                  setIsVisible(false);
                  setTimeout(() => {
                    setShowManagePreferences(true);
                    setTimeout(() => { setIsVisible(true); }, 50);
                  }, 150);
                }}
              >
                {t("managePreferences")}
              </button>
            }
          </div>
        </div>
      </div>
    </>
  );
};

const PrivacyConsent = () => {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (Cookies.get('eea') === '1') setEnabled(true);
  }, []);

  if (!enabled) return null;

  return <PrivacyConsentHandler />;
};

export default PrivacyConsent;
